import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Input } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import axios from 'axios';
import { messageErreur } from '../../../components/ndoe';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '../../../config';
import CataloguePicker from '../../journalTransactions/CataloguePicker';
import useRefLists from '../../rapports/useRefLists';
import canSeeFinance from '../../../utils/canSeeFinance';
import usePermissions from '../../../hooks/usePermissions';
import { NdoeImage } from '../../../components/ndoe';
import { customerStepValid, customerStepReason, totalPaires as computeTotalPaires } from './commandeWizardLogic';
import '../../pageLivreur/deliveryWizard.css';
import './commandeWizard.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const money = (v) => `${(Number(v) || 0).toLocaleString('fr-FR')} $`;
const pop = () => document.body; // les menus antd s'ancrent au body (pas de clipping mobile)

/**
 * Wizard de création de commande (demande proprio 21/07) — pendant mobile des
 * wizards livreur. Flux guidé : cliente → articles → panier ÉDITABLE → récap.
 *
 * Le PANIER est local et pleinement modifiable (quantité, prix, retrait) AVANT
 * toute écriture. Rien n'est envoyé au serveur avant « Créer la commande » ;
 * à ce moment le wizard orchestre les endpoints PROUVÉS et INCHANGÉS
 * (client si nouveau → commandePost en-tête → un detail-commande par ligne).
 * Aucune écriture stock/argent : une commande est une simple intention côté
 * serveur (stock réservé à l'affectation, argent à la livraison).
 *
 * Présentation (refonte UI 21/07, dossier « Refonte Commandes/Livreur ») :
 * esthétique premium ivoire/encre/or de la variante A + contrôle segmenté et
 * récap structuré de la variante C. Charte Ndoé conservée (ndoeTokens.css) ;
 * la LOGIQUE ci-dessus est strictement inchangée.
 */
export default function CommandeWizard({ onClose }) {
  const navigate = useNavigate();
  const user = useSelector((s) => s.user?.currentUser);
  const { can } = usePermissions();
  // Voir/éditer le prix : rôle finance (admin) OU permission dédiée réglable dans
  // les Paramètres (commandes.modifier_prix — accordable aux secrétaires).
  const peutPrix = canSeeFinance(user) || can('commandes.modifier_prix');
  const peutAcompte = can('reservations.acompte');
  const peutAjoutContact = can('clientes.creer'); // ajouter adresse/numéro = modifier la fiche cliente
  const refs = useRefLists(true); // référentiels du CataloguePicker (catégories/marques/couleurs)

  const [etape, setEtape] = useState('cliente'); // cliente | articles | panier | recap

  // ── Cliente & livraison ────────────────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [idClient, setIdClient] = useState(null);
  const [adresses, setAdresses] = useState([]);
  const [idAdresse, setIdAdresse] = useState(null);
  const [telephones, setTelephones] = useState([]);
  const [idTelephone, setIdTelephone] = useState(null);
  const [shops, setShops] = useState([]);
  const [idShop, setIdShop] = useState(null);
  // Nouvelle cliente (sous-formulaire replié par défaut).
  const [showNew, setShowNew] = useState(false);
  const [nc, setNc] = useState({ nom: '', telephone: '', id_province: null, id_commune: null, avenue: '', quartier: '', num: '' });
  const [provinces, setProvinces] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [savingClient, setSavingClient] = useState(false);
  // Ajout EN LIGNE d'une adresse / d'un numéro à la cliente DÉJÀ sélectionnée.
  const [addAdr, setAddAdr] = useState(null);   // null = fermé ; sinon {id_ville,id_commune,avenue,quartier,num}
  const [addAdrComm, setAddAdrComm] = useState([]);
  const [errAddAdrComm, setErrAddAdrComm] = useState(null);
  const [savingAdr, setSavingAdr] = useState(false);
  const [addTel, setAddTel] = useState(null);   // null = fermé ; sinon la chaîne saisie
  const [savingTel, setSavingTel] = useState(false);

  // ── Panier (local, éditable) ───────────────────────────────────────────────
  const [lignes, setLignes] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ajouts, setAjouts] = useState(0);
  // Acompte facultatif encaissé à la création (permission reservations.acompte).
  const [acMontant, setAcMontant] = useState('');
  const [acMoyen, setAcMoyen] = useState('cash');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Masquage de la barre de navigation basse pendant le wizard. Le CSS le fait
  // via `body:has(.cw)` (WebView modernes) ; ce marqueur de classe est le repli
  // pour un ancien WebView Android sans support de :has().
  useEffect(() => {
    document.body.classList.add('cw-open');
    return () => document.body.classList.remove('cw-open');
  }, []);

  // Référentiels de base : clientes, boutiques (pré-sélection défaut), provinces.
  useEffect(() => {
    (async () => {
      try {
        const [c, s, p] = await Promise.all([
          axios.get(`${DOMAIN}/api/client`),
          axios.get(`${DOMAIN}/api/shop`),
          axios.get(`${DOMAIN}/api/livreur/province`),
        ]);
        setClients(Array.isArray(c.data) ? c.data : []);
        const sh = Array.isArray(s.data) ? s.data : [];
        setShops(sh);
        const def = sh.find((x) => x.par_defaut) || (sh.length === 1 ? sh[0] : null);
        if (def) setIdShop(def.id_shop);
        setProvinces(Array.isArray(p.data) ? p.data : []);
      } catch (e) { /* référentiels best-effort */ }
    })();
  }, []);

  // Communes dépendantes de la province (nouvelle cliente).
  useEffect(() => {
    if (!nc.id_province) { setCommunes([]); return; }
    (async () => {
      try { const { data } = await axios.get(`${DOMAIN}/api/livreur/commune/${nc.id_province}`); setCommunes(Array.isArray(data) ? data : []); }
      catch (e) { setCommunes([]); setError(messageErreur(e, 'les communes de cette province')); }
    })();
  }, [nc.id_province]);

  // Adresses + téléphones de la cliente sélectionnée (pré-sélection du 1er).
  const chargerContacts = useCallback(async (cid) => {
    // Vidage SYNCHRONE : au changement de cliente, l'adresse/téléphone de la
    // précédente ne doit jamais subsister le temps du fetch (risque de valider
    // une commande avec les contacts de quelqu'un d'autre).
    setAdresses([]); setIdAdresse(null); setTelephones([]); setIdTelephone(null);
    if (!cid) return;
    try {
      const [a, t] = await Promise.all([
        axios.get(`${DOMAIN}/api/client/clientAdresse/${cid}`),
        axios.get(`${DOMAIN}/api/client/clientTelephone/${cid}`),
      ]);
      const la = Array.isArray(a.data) ? a.data : [];
      const lt = Array.isArray(t.data) ? t.data : [];
      setAdresses(la); setIdAdresse(la[0]?.id_adresse ?? null);
      setTelephones(lt); setIdTelephone(lt[0]?.id_telephone ?? null);
    } catch (e) {
      // UNE PANNE N'EST PAS UNE CLIENTE SANS ADRESSE (10/08/2026).
      // Sans message, les listes vides ressemblaient à « cette cliente n'a pas
      // d'adresse enregistrée » — et l'on pouvait engager une commande en
      // croyant qu'il n'y avait rien à choisir.
      setAdresses([]); setTelephones([]);
      setError(messageErreur(e, 'les adresses et téléphones de cette cliente'));
    }
  }, []);

  const choisirClient = (v) => { setIdClient(v || null); setError(''); chargerContacts(v); };

  const clientOptions = useMemo(() => clients.map((c) => ({ value: c.id, label: c.telephone ? `${c.nom} — ${c.telephone}` : c.nom })), [clients]);
  const adresseOptions = useMemo(() => adresses.map((a) => ({
    value: a.id_adresse, label: `${a.nom_province || ''} · C/${a.nom_commune || ''} Av/${a.avenue || ''} Q/${a.quartier || ''} N°${a.num || ''}`,
  })), [adresses]);
  const clientNom = useMemo(() => clients.find((c) => c.id === idClient)?.nom || '', [clients, idClient]);

  const creerCliente = async () => {
    if (!nc.nom.trim()) { toast.warning('Le nom de la cliente est obligatoire.'); return; }
    if (!nc.telephone.trim()) { toast.warning('Le téléphone est obligatoire.'); return; }
    // Une adresse est INDISPENSABLE : sans elle, aucun id_adresse ne sera créé et
    // la commande ne pourra pas être validée (livraison sans adresse impossible).
    if (!nc.id_province || !nc.id_commune || !nc.avenue.trim()) {
      toast.warning('L’adresse de livraison est obligatoire (province, commune, avenue).');
      return;
    }
    setSavingClient(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/client/client`, {
        nom: nc.nom, telephone: nc.telephone, id_province: nc.id_province, id_commune: nc.id_commune,
        commune: nc.id_commune, avenue: nc.avenue, quartier: nc.quartier, num: nc.num,
      });
      const nouvel = data?.id_client;
      const { data: liste } = await axios.get(`${DOMAIN}/api/client`);
      setClients(Array.isArray(liste) ? liste : []);
      if (nouvel) { setIdClient(nouvel); await chargerContacts(nouvel); }
      setShowNew(false);
      setNc({ nom: '', telephone: '', id_province: null, id_commune: null, avenue: '', quartier: '', num: '' });
      toast.success('Cliente créée et sélectionnée.');
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Création de la cliente impossible.');
    } finally { setSavingClient(false); }
  };

  // Communes de la province choisie pour l'ajout d'adresse en ligne.
  const addAdrVille = addAdr && addAdr.id_ville;
  useEffect(() => {
    if (!addAdrVille) { setAddAdrComm([]); setErrAddAdrComm(null); return; }
    (async () => {
      try { const { data } = await axios.get(`${DOMAIN}/api/livreur/commune/${addAdrVille}`); setAddAdrComm(Array.isArray(data) ? data : []); }
      catch (e) { setAddAdrComm([]); setErrAddAdrComm(messageErreur(e, 'les communes de cette ville')); }
    })();
  }, [addAdrVille]);

  const ajouterAdresse = async () => {
    if (savingAdr || !idClient) return;
    if (!addAdr || !addAdr.id_ville || !addAdr.id_commune || !String(addAdr.avenue || '').trim()) {
      toast.warning('Province, commune et avenue sont requises.'); return;
    }
    setSavingAdr(true);
    try {
      await axios.post(`${DOMAIN}/api/client/clientAdresse`, {
        id_client: idClient, id_ville: addAdr.id_ville, id_commune: addAdr.id_commune,
        avenue: addAdr.avenue || '', quartier: addAdr.quartier || '', num: addAdr.num || '', ref: null,
      });
      const { data } = await axios.get(`${DOMAIN}/api/client/clientAdresse/${idClient}`);
      const la = Array.isArray(data) ? data : [];
      setAdresses(la);
      const cree = la.reduce((a, b) => (a && Number(a.id_adresse) > Number(b.id_adresse) ? a : b), null);
      if (cree) setIdAdresse(cree.id_adresse);
      setAddAdr(null); setAddAdrComm([]);
      toast.success('Adresse ajoutée.');
    } catch (e) { toast.error(e.response?.data?.error || 'Ajout d’adresse impossible.'); }
    finally { setSavingAdr(false); }
  };

  const ajouterTelephone = async () => {
    if (savingTel || !idClient) return;
    const numero = String(addTel || '').trim();
    if (!numero) { toast.warning('Saisis un numéro.'); return; }
    setSavingTel(true);
    try {
      await axios.post(`${DOMAIN}/api/client/clientTelephone`, { id_client: idClient, numero });
      const { data } = await axios.get(`${DOMAIN}/api/client/clientTelephone/${idClient}`);
      const lt = Array.isArray(data) ? data : [];
      setTelephones(lt);
      const cree = [...lt].reverse().find((t) => String(t.numero) === numero) || lt[lt.length - 1];
      if (cree) setIdTelephone(cree.id_telephone);
      setAddTel(null);
      toast.success('Numéro ajouté.');
    } catch (e) { toast.error(e.response?.data?.error || 'Ajout du numéro impossible.'); }
    finally { setSavingTel(false); }
  };

  // ── Panier ─────────────────────────────────────────────────────────────────
  const ajouterAuPanier = (v) => {
    setLignes((prev) => {
      const i = prev.findIndex((l) => l.id_varianteProduit === v.id_varianteProduit && l.id_taille === v.id_taille);
      if (i >= 0) {
        const c = [...prev]; c[i] = { ...c[i], quantite: (Number(c[i].quantite) || 0) + (Number(v.quantite) || 1) }; return c;
      }
      return [...prev, {
        id_varianteProduit: v.id_varianteProduit, id_taille: v.id_taille, taille: v.taille, couleur: v.couleur,
        code_article: v.code_article, nom_produit: v.nom_produit, img: v.img,
        // Prix PROMO s'il y en a un — voir Commande.jsx : la promotion n'était
        // appliquée nulle part, le prix de base partait tel quel. Le panier
        // porte donc désormais le prix retenu, et c'est lui qui sera enregistré.
        prix: Number(v.prix_promo ?? v.prix) || 0, quantite: Number(v.quantite) || 1,
      }];
    });
    setAjouts((n) => n + 1);
  };
  const majQte = (i, d) => setLignes((p) => p.map((l, k) => (k === i ? { ...l, quantite: Math.max(1, (Number(l.quantite) || 1) + d) } : l)));
  const majPrix = (i, val) => setLignes((p) => p.map((l, k) => (k === i ? { ...l, prix: val === '' ? '' : Math.max(0, Number(val) || 0) } : l)));
  // Motif de gratuité — exigé dès qu'une ligne passe à 0. Sans lui, personne ne
  // saura dans six mois pourquoi cette paire est sortie sans être payée.
  const majMotif = (i, val) => setLignes((p) => p.map((l, k) => (k === i ? { ...l, motif_gratuite: val.slice(0, 120) } : l)));
  const retirer = (i) => setLignes((p) => p.filter((_, k) => k !== i));

  const totalPaires = computeTotalPaires(lignes);
  const totalMontant = lignes.reduce((s, l) => s + (Number(l.prix) || 0) * (Number(l.quantite) || 0), 0);

  // Étape cliente : valide SEULEMENT si cliente + adresse + téléphone + boutique
  // ET onglet « existante » (le garde-fou !showNew empêche d'avancer sur
  // l'ancienne cliente après avoir basculé sur « Nouvelle cliente »). La boutique
  // est désormais OBLIGATOIRE.
  const custState = { showNew, idClient, idAdresse, idTelephone, idShop };
  const custValid = customerStepValid(custState);
  const custReason = customerStepReason(custState);

  // Libellés du récapitulatif final : la cliente doit VOIR adresse/téléphone/
  // boutique avant de confirmer (pas seulement le nom).
  const clientTel = useMemo(() => telephones.find((t) => t.id_telephone === idTelephone)?.numero || '', [telephones, idTelephone]);
  const adresseTxt = useMemo(() => adresseOptions.find((o) => o.value === idAdresse)?.label || '', [adresseOptions, idAdresse]);
  const shopTxt = useMemo(() => shops.find((s) => s.id_shop === idShop)?.nom || '', [shops, idShop]);

  // ── Validation : orchestration des endpoints prouvés ───────────────────────
  // Le serveur rejette un prix ou une quantité falsy (0). On bloque en amont.
  // ⚠ LE ZÉRO N'EST PLUS UNE ERREUR — c'est une paire OFFERTE (cadeau,
  // remplacement, geste commercial). Il reste réservé à qui détient
  // « Modifier le prix », et exige un motif : c'est ce que le serveur vérifie
  // aussi, on ne fait ici que l'annoncer plus tôt.
  //
  // Avant, une ligne à 0 était bloquée sans explication utile, et le formulaire
  // de bureau, lui, remplaçait silencieusement la saisie par le prix catalogue.
  const ligneOfferte = (l) => Number(l.prix) === 0 && l.prix !== '';
  // Ce qui rend UNE ligne fautive — la même règle que le bouton, pour que le
  // rouge désigne exactement ce qui bloque.
  const ligneInvalide = (l) => {
    if (!(Number(l.quantite) > 0)) return true;
    if (l.prix === '' || l.prix === null || l.prix === undefined) return true;
    if (ligneOfferte(l)) return !peutPrix || !String(l.motif_gratuite || '').trim();
    return !(Number(l.prix) > 0);
  };
  const prixInvalide = lignes.some((l) => {
    if (!(Number(l.quantite) > 0)) return true;
    if (l.prix === '' || l.prix === null || l.prix === undefined) return true;
    if (ligneOfferte(l)) return !peutPrix || !String(l.motif_gratuite || '').trim();
    return !(Number(l.prix) > 0);
  });

  const valider = async () => {
    if (saving) return;
    // Garde étape cliente (inclut la boutique OBLIGATOIRE) : renvoie à l'étape
    // avec la raison précise (ex. « Choisis la boutique concernée. »).
    if (!custValid) { setEtape('cliente'); setError(custReason || 'Complète l’étape cliente (cliente, adresse, téléphone, boutique).'); return; }
    if (!lignes.length) { setEtape('panier'); setError('Ajoute au moins un article.'); return; }
    if (prixInvalide) { setEtape('panier'); setError('Chaque ligne doit avoir un prix et une quantité supérieurs à 0.'); return; }
    setSaving(true); setError('');
    let idc = null;
    try {
      const { data: hdr } = await axios.post(`${DOMAIN}/api/commande/commandePost`, {
        id_client: idClient, id_shop: idShop, id_adresse: idAdresse, id_telephone: idTelephone, user_cr: user?.id,
      });
      idc = hdr?.id_commande;
      if (!idc) throw new Error('Commande non créée.');
      // Une ligne par article (le panier a déjà agrégé variante+pointure → pas de 409).
      for (const l of lignes) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${DOMAIN}/api/commande/detail-commande`, {
          id_commande: idc, id_varianteProduit: l.id_varianteProduit, id_taille: l.id_taille,
          prix: Number(l.prix) || 0, quantite: Number(l.quantite) || 1, user_cr: user?.id,
          // Transmis uniquement quand la paire est offerte ; le serveur l'exige alors.
          motif_gratuite: Number(l.prix) === 0 ? (l.motif_gratuite || '').trim() : undefined,
        });
      }
      // Acompte facultatif : encaissé APRÈS la commande, il ne la fait jamais
      // échouer (elle est déjà créée). Imputé sur la dette à la livraison.
      const ac = Number(acMontant) || 0;
      if (ac > 0 && peutAcompte) {
        try {
          await axios.post(`${DOMAIN}/api/acomptes`, { id_commande: idc, montant: ac, moyen: acMoyen });
        } catch (e) {
          toast.warning('Commande créée, mais l’acompte n’a pas pu être enregistré : ' + (e.response?.data?.error || e.response?.data?.message || 'erreur'));
        }
      }
      toast.success(`Commande #${idc} créée · ${totalPaires} paire(s).`);
      navigate(`/listeDetailView/${idc}`); // enchaîne sur l'affectation livreur, comme aujourd'hui
    } catch (e) {
      // L'en-tête a pu être créé mais une ligne a échoué (réseau) : plutôt que de
      // laisser un état partiel bloqué (re-soumettre ferait un doublon d'en-tête),
      // on bascule sur la page d'ajout d'articles existante pour finir sans perte.
      if (idc) {
        // La commande existe : on tente quand même d'enregistrer l'acompte
        // encaissé (best-effort) pour ne pas perdre l'argent, puis on oriente vers
        // la page d'ajout d'articles pour finir les lignes manquantes.
        const acp = Number(acMontant) || 0;
        if (acp > 0 && peutAcompte) {
          try { await axios.post(`${DOMAIN}/api/acomptes`, { id_commande: idc, montant: acp, moyen: acMoyen }); } catch (_) { /* déjà en erreur */ }
        }
        toast.warning('Commande créée, mais un article n’a pas pu être ajouté. Termine l’ajout sur la page commande.');
        navigate(`/commandes/${idc}`);
        return;
      }
      const msg = e.response?.data?.message || e.response?.data?.error || 'Création de la commande impossible.';
      // Un « déjà existante » (400) = l'en-tête a presque sûrement été créé (réponse
      // perdue après commit) : on évite un re-clic qui rebutera sur le doublon.
      setError(/déjà existante/i.test(msg) ? 'Cette commande semble déjà créée — vérifie la liste des commandes avant de recommencer.' : msg);
    } finally { setSaving(false); }
  };

  // ── Présentation : chrome commun (intro premium + stepper nommé) ───────────
  const STEPS = [
    { key: 'cliente', n: 1, label: 'Cliente' },
    { key: 'articles', n: 2, label: 'Articles' },
    { key: 'panier', n: 3, label: 'Panier' },
    { key: 'recap', n: 4, label: 'Valider' },
  ];
  const curStep = STEPS.findIndex((s) => s.key === etape);
  const stepper = (
    <ol className="cw-steps" aria-label={`Étape ${curStep + 1} sur ${STEPS.length}`}>
      {STEPS.map((s, i) => (
        <li key={s.key} className={`cw-stp${i === curStep ? ' is-current' : ''}${i < curStep ? ' is-done' : ''}`} aria-current={i === curStep ? 'step' : undefined}>
          <span className="cw-stp__n">{i < curStep ? '✓' : s.n}</span>
          <small>{s.label}</small>
        </li>
      ))}
    </ol>
  );
  // Invoqué (pas monté comme <Composant/>) : le JSX est inliné → aucun remontage
  // ni perte de focus des champs (piège connu des sous-composants dans le render).
  const head = (back, backLabel, helper) => (
    <>
      <button type="button" className="cw-back" onClick={back}>← {backLabel}</button>
      <header className="cw-intro">
        <span className="cw-eyebrow">Nouvelle commande</span>
        <h1 className="cw-h1">Créer une commande</h1>
        <p className="cw-sub">
          {clientNom
            ? <>Pour <b>{clientNom}</b>{helper ? ` · ${helper}` : ''}</>
            : (helper || 'Identifiez la cliente, puis composez le panier.')}
        </p>
      </header>
      {stepper}
    </>
  );
  const errCta = error ? <p className="dw-error" role="alert">{error}</p> : null;

  // ── Étape cliente ──────────────────────────────────────────────────────────
  if (etape === 'cliente') {
    return (
      <main className="dw cw">
        {head(onClose, 'Annuler', 'Choisissez la cliente et la livraison.')}
        <div className="dw-step">
          <h3 className="cw-q">Pour quelle cliente&nbsp;?</h3>

          <section className="cw-card">
            <div className="cw-seg" role="tablist" aria-label="Type de cliente">
              <button type="button" role="tab" aria-selected={!showNew} className={`cw-seg__b${!showNew ? ' is-on' : ''}`} onClick={() => setShowNew(false)}>Cliente existante</button>
              <button type="button" role="tab" aria-selected={showNew} className={`cw-seg__b${showNew ? ' is-on' : ''}`} onClick={() => setShowNew(true)}>Nouvelle cliente</button>
            </div>

            {!showNew ? (
              <div className="cw-seg__panel">
                <label className="cw-lbl" htmlFor="cw-cli">Rechercher</label>
                <Select id="cw-cli" showSearch className="cw-select" placeholder="Nom ou téléphone…" optionFilterProp="label"
                  options={clientOptions} value={idClient} onChange={choisirClient} allowClear getPopupContainer={pop} />
              </div>
            ) : (
              <div className="cw-seg__panel cw-newclient">
                <label className="cw-lbl" htmlFor="cw-nc-nom">Nom de la cliente *</label>
                <Input id="cw-nc-nom" className="cw-in" placeholder="Nom complet" value={nc.nom} onChange={(e) => setNc((s) => ({ ...s, nom: e.target.value }))} />
                <label className="cw-lbl" htmlFor="cw-nc-tel">Téléphone *</label>
                <Input id="cw-nc-tel" className="cw-in" placeholder="Ex. 0812345678" inputMode="tel" value={nc.telephone} onChange={(e) => setNc((s) => ({ ...s, telephone: e.target.value }))} />
                <label className="cw-lbl" htmlFor="cw-nc-prov">Province *</label>
                <Select id="cw-nc-prov" className="cw-select" placeholder="Choisir la province" optionFilterProp="label" showSearch getPopupContainer={pop}
                  options={provinces.map((p) => ({ value: p.id_province, label: p.nom_province }))}
                  value={nc.id_province} onChange={(v) => setNc((s) => ({ ...s, id_province: v, id_commune: null }))} />
                <label className="cw-lbl" htmlFor="cw-nc-comm">Commune *</label>
                <Select id="cw-nc-comm" className="cw-select" placeholder="Choisir la commune" optionFilterProp="label" showSearch getPopupContainer={pop}
                  options={communes.map((c) => ({ value: c.id_commune, label: c.nom_commune }))}
                  value={nc.id_commune} onChange={(v) => setNc((s) => ({ ...s, id_commune: v }))} />
                <label className="cw-lbl" htmlFor="cw-nc-av">Avenue *</label>
                <Input id="cw-nc-av" className="cw-in" placeholder="Nom de l'avenue" value={nc.avenue} onChange={(e) => setNc((s) => ({ ...s, avenue: e.target.value }))} />
                <div className="cw-row2">
                  <div>
                    <label className="cw-lbl" htmlFor="cw-nc-q">Quartier</label>
                    <Input id="cw-nc-q" className="cw-in" placeholder="Quartier" value={nc.quartier} onChange={(e) => setNc((s) => ({ ...s, quartier: e.target.value }))} />
                  </div>
                  <div>
                    <label className="cw-lbl" htmlFor="cw-nc-num">N°</label>
                    <Input id="cw-nc-num" className="cw-in" placeholder="N°" value={nc.num} onChange={(e) => setNc((s) => ({ ...s, num: e.target.value }))} />
                  </div>
                </div>
                <button type="button" className="dw-btn primary cw-createbtn" disabled={savingClient} onClick={creerCliente}>
                  {savingClient ? 'Création…' : 'Créer la cliente'}
                </button>
              </div>
            )}
          </section>

          {idClient && !showNew && (
            <section className="cw-card">
              <div className="cw-cardhead"><span className="cw-cardhead__ic">📍</span><h4 className="cw-cardhead__t">Livraison &amp; boutique</h4></div>

              <div className="cw-selected" aria-label="Cliente sélectionnée">
                <span className="cw-selected__ava" aria-hidden="true">{(clientNom || '?').charAt(0).toUpperCase()}</span>
                <span className="cw-selected__t"><b>{clientNom}</b><small>{clientTel || 'Aucun numéro sélectionné'}</small></span>
              </div>

              <label className="cw-lbl">Adresse de livraison</label>
              <Select className="cw-select" placeholder="Choisir l'adresse" optionFilterProp="label" showSearch getPopupContainer={pop}
                options={adresseOptions} value={idAdresse} onChange={(v) => setIdAdresse(v || null)}
                notFoundContent="Aucune adresse enregistrée" />
              {peutAjoutContact && !addAdr && (
                <button type="button" className="cw-addlink" onClick={() => setAddAdr({ id_ville: null, id_commune: null, avenue: '', quartier: '', num: '' })}>
                  ➕ Ajouter une adresse{!adresses.length ? ' (cette cliente n’en a pas)' : ''}
                </button>
              )}
              {!peutAjoutContact && !adresses.length && <p className="dw-hint" style={{ margin: '4px 0 0' }}>Cette cliente n’a pas d’adresse — fais-la ajouter par une personne habilitée avant de continuer.</p>}
              {addAdr && (
                <div className="cw-newclient">
                  <Select className="cw-select" placeholder="Province" optionFilterProp="label" showSearch getPopupContainer={pop}
                    options={provinces.map((p) => ({ value: p.id_province, label: p.nom_province }))}
                    value={addAdr.id_ville} onChange={(v) => setAddAdr((s) => ({ ...s, id_ville: v, id_commune: null }))} />
                  <Select className="cw-select" placeholder="Commune" optionFilterProp="label" showSearch getPopupContainer={pop}
                    options={addAdrComm.map((c) => ({ value: c.id_commune, label: c.nom_commune }))}
                    notFoundContent={errAddAdrComm || undefined}
                    value={addAdr.id_commune} onChange={(v) => setAddAdr((s) => ({ ...s, id_commune: v }))} />
                  <Input className="cw-in" placeholder="Avenue" value={addAdr.avenue} onChange={(e) => setAddAdr((s) => ({ ...s, avenue: e.target.value }))} />
                  <div className="cw-row2">
                    <Input className="cw-in" placeholder="Quartier" value={addAdr.quartier} onChange={(e) => setAddAdr((s) => ({ ...s, quartier: e.target.value }))} />
                    <Input className="cw-in" placeholder="N°" value={addAdr.num} onChange={(e) => setAddAdr((s) => ({ ...s, num: e.target.value }))} />
                  </div>
                  <div className="cw-row2">
                    <button type="button" className="dw-btn ghost" onClick={() => setAddAdr(null)}>Annuler</button>
                    <button type="button" className="dw-btn primary" disabled={savingAdr} onClick={ajouterAdresse}>{savingAdr ? 'Ajout…' : 'Ajouter l’adresse'}</button>
                  </div>
                </div>
              )}

              <label className="cw-lbl">Téléphone</label>
              <Select className="cw-select" placeholder="Choisir le téléphone" getPopupContainer={pop}
                options={telephones.map((t) => ({ value: t.id_telephone, label: t.numero }))} value={idTelephone} onChange={(v) => setIdTelephone(v || null)}
                suffixIcon={<PhoneOutlined />} />
              {!peutAjoutContact ? null : addTel === null
                ? <button type="button" className="cw-addlink" onClick={() => setAddTel('')}>➕ Ajouter un numéro</button>
                : (
                  <div className="cw-row2" style={{ marginTop: 6 }}>
                    <Input className="cw-in" placeholder="Nouveau numéro" inputMode="tel" value={addTel} onChange={(e) => setAddTel(e.target.value)} />
                    <button type="button" className="dw-btn primary" disabled={savingTel} onClick={ajouterTelephone} style={{ flex: '0 0 auto', padding: '0 14px' }}>{savingTel ? '…' : 'OK'}</button>
                  </div>
                )}

              <label className="cw-lbl">Boutique</label>
              <Select className="cw-select" placeholder="Boutique" optionFilterProp="label" showSearch getPopupContainer={pop}
                options={shops.map((s) => ({ value: s.id_shop, label: s.nom + (s.commune ? ` — ${s.commune}` : '') }))}
                value={idShop} onChange={(v) => setIdShop(v || null)} />
            </section>
          )}
        </div>
        <div className="dw-cta">
          {errCta}
          {!custValid && custReason && <p className="dw-hint cw-ctareason">{custReason}</p>}
          <button type="button" className="dw-btn primary" disabled={!custValid} onClick={() => setEtape('articles')}>Continuer vers les articles →</button>
        </div>
      </main>
    );
  }

  // ── Étape articles ─────────────────────────────────────────────────────────
  if (etape === 'articles') {
    return (
      <main className="dw cw">
        {head(() => setEtape('cliente'), 'Cliente', 'Composez le panier.')}
        <div className="dw-step">
          <h3 className="cw-q">Quels articles&nbsp;?</h3>
          <section className="cw-card cw-card--flat">
            <button type="button" className="cw-openpicker" onClick={() => setPickerOpen(true)}>
              <span className="cw-openpicker__ic">📖</span>
              <span className="cw-openpicker__t"><b>Ouvrir le catalogue</b><small>Article, pointure et quantité — il tombe au panier</small></span>
              <span className="cw-openpicker__chev">→</span>
            </button>
          </section>
          {lignes.length > 0 && (
            <section className="cw-card">
              <div className="cw-cardhead"><span className="cw-cardhead__ic">🛍️</span><h4 className="cw-cardhead__t">Déjà au panier · {totalPaires} paire{totalPaires > 1 ? 's' : ''}</h4></div>
              <div className="cw-cartmini">
                {lignes.map((l) => (
                  <div className="cw-mini" key={`${l.id_varianteProduit}-${l.id_taille}`}>
                    <NdoeImage src={l.img} alt={l.nom_produit} size="sm" preview={false} />
                    <span className="cw-mini__t"><b>{l.nom_produit}</b><small>{l.couleur || '—'} · P{l.taille} · ×{l.quantite}</small></span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        <div className="dw-cta">
          {errCta}
          <button type="button" className="dw-btn primary" disabled={!lignes.length} onClick={() => setEtape('panier')}>
            Voir le panier{totalPaires ? ` (${totalPaires} paire${totalPaires > 1 ? 's' : ''})` : ''} →
          </button>
        </div>
        <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refs}
          avecPrix={peutPrix} avecQuantite bloquerRupture compteur={ajouts} onSelect={ajouterAuPanier} />
      </main>
    );
  }

  // ── Étape panier (éditable) ────────────────────────────────────────────────
  if (etape === 'panier') {
    return (
      <main className="dw cw">
        {head(() => setEtape('articles'), 'Articles', 'Ajustez quantités et prix.')}
        <div className="dw-step">
          <h3 className="cw-q">Le panier</h3>
          <div className="cw-lines">
            {lignes.map((l, i) => (
              <div className={`cw-line${ligneInvalide(l) ? ' is-invalid' : ''}`} key={`${l.id_varianteProduit}-${l.id_taille}`}>
                <NdoeImage src={l.img} alt={l.nom_produit} size="sm" preview={false} />
                <div className="cw-line__t">
                  <b>{l.nom_produit}</b>
                  <small>{l.couleur || '—'} · P{l.taille}</small>
                  {peutPrix && (
                    <div className="cw-price">
                      <input inputMode="numeric" aria-label="Prix unitaire" value={l.prix}
                        onChange={(e) => majPrix(i, e.target.value.replace(/\D/g, ''))} />
                      <span>$</span>
                    </div>
                  )}
                  {/* PAIRE OFFERTE : le champ n'apparaît qu'à 0, là où la
                      question se pose. L'afficher en permanence ferait un
                      formulaire encombré pour un cas rare. */}
                  {peutPrix && ligneOfferte(l) && (
                    <div className="cw-offerte">
                      <span className="cw-offerte__tag">Offerte</span>
                      <input
                        aria-label="Motif de la gratuité"
                        placeholder="Pourquoi ? (cadeau, remplacement…)"
                        value={l.motif_gratuite || ''}
                        maxLength={120}
                        onChange={(e) => majMotif(i, e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="cw-stepper">
                  <button type="button" aria-label="Retirer une paire" onClick={() => majQte(i, -1)}>−</button>
                  <span>{l.quantite}</span>
                  <button type="button" aria-label="Ajouter une paire" onClick={() => majQte(i, 1)}>+</button>
                </div>
                <button type="button" className="cw-rm" onClick={() => retirer(i)} aria-label="Retirer l'article">✕</button>
              </div>
            ))}
            {!lignes.length && <p className="dw-hint">Panier vide — reviens ajouter des articles.</p>}
          </div>
          <button type="button" className="cw-addbtn" onClick={() => setEtape('articles')}>＋ Ajouter un article</button>
        </div>
        <div className="dw-cta">
          {errCta}
          {prixInvalide && (
            <p className="cw-warnprix" role="alert">
              {peutPrix
                ? 'Un article n’a pas de prix — corrige-le (chaque ligne doit être > 0).'
                : 'Un article n’a pas de prix catalogue — retire-le (✕) ou fais-le prixer par un responsable.'}
            </p>
          )}
          {peutPrix && <div className="dw-recap__tot cw-tot">💵 Total <b>{money(totalMontant)}</b></div>}
          <button type="button" className="dw-btn primary" disabled={!lignes.length || prixInvalide} onClick={() => setEtape('recap')}>Continuer →</button>
        </div>
      </main>
    );
  }

  // ── Étape récap ────────────────────────────────────────────────────────────
  return (
    <main className="dw cw">
      {head(() => setEtape('panier'), 'Panier', 'Dernière vérification.')}
      <div className="dw-step">
        <h3 className="cw-q">On valide la commande&nbsp;?</h3>
        <section className="cw-card">
          <div className="cw-cardhead"><span className="cw-cardhead__ic">🧾</span><h4 className="cw-cardhead__t">Récapitulatif</h4></div>
          <div className="dw-recap">
            <div className="dw-recap__line"><span className="dw-recap__dot tone-ok" /><span className="dw-recap__t">{clientNom}</span><b className="dw-recap__b">Cliente</b></div>
            {adresseTxt && <div className="dw-recap__line"><span className="dw-recap__dot tone-muted" /><span className="dw-recap__t">{adresseTxt}</span><b className="dw-recap__b">Adresse</b></div>}
            {clientTel && <div className="dw-recap__line"><span className="dw-recap__dot tone-muted" /><span className="dw-recap__t">{clientTel}</span><b className="dw-recap__b">Téléphone</b></div>}
            {shopTxt && <div className="dw-recap__line"><span className="dw-recap__dot tone-muted" /><span className="dw-recap__t">{shopTxt}</span><b className="dw-recap__b">Boutique</b></div>}
            {lignes.map((l) => (
              <div className="dw-recap__line" key={`${l.id_varianteProduit}-${l.id_taille}`}>
                <span className="dw-recap__dot tone-muted" />
                <span className="dw-recap__t">{l.nom_produit} · P{l.taille} ×{l.quantite}</span>
                {peutPrix && <b className="dw-recap__b">{money((Number(l.prix) || 0) * (Number(l.quantite) || 0))}</b>}
              </div>
            ))}
            {peutPrix && <div className="dw-recap__tot">Total attendu <b>{money(totalMontant)}</b></div>}
          </div>
        </section>

        {peutAcompte && (
          <section className="cw-card cw-acompte">
            <div className="cw-cardhead"><span className="cw-cardhead__ic">💰</span><h4 className="cw-cardhead__t">Acompte encaissé <em>(facultatif)</em></h4></div>
            <div className="cw-acompte__row">
              <div className="cw-acompte__mnt">
                <input inputMode="numeric" aria-label="Montant de l'acompte" placeholder="0" value={acMontant}
                  onChange={(e) => setAcMontant(e.target.value.replace(/\D/g, ''))} />
                <span>$</span>
              </div>
              <Select className="cw-acompte__moyen" value={acMoyen} onChange={setAcMoyen} getPopupContainer={pop}
                options={[{ value: 'cash', label: 'Cash' }, { value: 'mpesa', label: 'M-Pesa' }, { value: 'airtel', label: 'Airtel Money' }, { value: 'orange', label: 'Orange Money' }, { value: 'banque', label: 'Banque' }]} />
            </div>
            {peutPrix && Number(acMontant) > 0 && Number(acMontant) > totalMontant && (
              <p className="cw-warnprix">L’acompte dépasse le total de la commande.</p>
            )}
            <p className="dw-hint" style={{ margin: '4px 0 0' }}>Dollars entiers. Il sera imputé sur la dette à la livraison.</p>
          </section>
        )}

        <p className="dw-hint">Aucun stock bloqué ni caisse touchée — la commande est une intention. Tu pourras affecter un livreur juste après.</p>
      </div>
      <div className="dw-cta">
        {errCta}
        <button type="button" className="dw-btn primary" disabled={saving || !lignes.length || !custValid || prixInvalide} onClick={valider}>
          {saving ? 'Création…' : '✓ Créer la commande'}
        </button>
        <button type="button" className="dw-btn ghost" onClick={() => setEtape('panier')}>← Corriger le panier</button>
      </div>
    </main>
  );
}
